import React from 'react';
import { ArrowRight, Cloud, Layers, Sparkles, ChevronRight } from 'lucide-react';

const PILLAR_ICONS = {
  'cloud-strategy': Cloud,
  'delivery-ops': Layers,
  'ai-solutions': Sparkles,
};

/**
 * Homepage Services — premium pillar layout; opens services mega overlay.
 */
export default function SectionServices({ content, overlayContent, onOpenServicesOverlay, onNavigateToService }) {
  const services = content || {};
  const heading = services.heading ?? 'Our services';
  const eyebrow = services.eyebrow ?? 'What we deliver';
  const subtext = services.subtext ?? '';
  const ctaText = services.ctaText ?? 'Browse all services';
  const pillars = Array.isArray(services.pillars) ? services.pillars : [];
  const groups = Array.isArray(overlayContent?.groups) ? overlayContent.groups : [];

  if (pillars.length === 0 && !heading) return null;

  return (
    <section
      id="services"
      className="py-14 sm:py-20 border-t"
      style={{ background: 'var(--jb-section-alt)', borderColor: 'var(--jb-border)' }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="mb-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="font-mono text-xs text-cyan uppercase tracking-widest mb-2">{eyebrow}</p>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl md:text-4xl text-text-hero mb-3 tracking-tight">
              {heading}
            </h2>
            {subtext && <p className="font-body text-text-sub text-base sm:text-lg leading-relaxed">{subtext}</p>}
          </div>
          {onOpenServicesOverlay && (
            <button
              type="button"
              onClick={onOpenServicesOverlay}
              className="jb-cta inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-sm hover:opacity-90 transition self-start lg:self-auto"
            >
              {ctaText} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {pillars.map((pillar, i) => {
            const Icon = PILLAR_ICONS[pillar.id] || Layers;
            const group = groups.find((g) => g.id === pillar.id);
            const items = Array.isArray(group?.items) ? group.items.slice(0, 4) : [];
            const highlights = Array.isArray(pillar.highlights) ? pillar.highlights : [];

            return (
              <div
                key={pillar.id || i}
                className="flex flex-col rounded-2xl border bg-forest-card overflow-hidden"
                style={{ borderColor: 'var(--jb-border)' }}
              >
                <button
                  type="button"
                  onClick={onOpenServicesOverlay}
                  className="group text-left p-6 sm:p-7 hover:bg-white/[0.02] transition"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: i % 2 === 0 ? 'var(--jb-cyan-dim)' : 'var(--jb-accent-green-dim)' }}
                  >
                    <Icon className="w-6 h-6 text-cyan" />
                  </div>
                  <h3 className="font-display font-extrabold text-xl text-text-hero mb-2 group-hover:text-cyan transition-colors">
                    {pillar.title}
                  </h3>
                  {pillar.description && (
                    <p className="font-body text-sm text-text-sub leading-relaxed mb-4">
                      {pillar.description}
                    </p>
                  )}
                  {highlights.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {highlights.map((tag) => (
                        <span
                          key={tag}
                          className="font-mono text-[0.65rem] uppercase tracking-wider px-2.5 py-1 rounded-full"
                          style={{ background: 'var(--jb-cyan-dim)', color: 'var(--jb-cyan)' }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </button>

                {items.length > 0 && (
                  <div className="border-t px-4 py-3 space-y-1 mt-auto" style={{ borderColor: 'var(--jb-border)' }}>
                    {items.map((item) => (
                      <button
                        key={item.slug}
                        type="button"
                        onClick={() =>
                          onNavigateToService
                            ? onNavigateToService(item.slug)
                            : (window.location.hash = `#service/${item.slug}`)
                        }
                        className="group w-full flex items-center justify-between gap-2 px-2 py-2.5 rounded-lg text-left hover:bg-cyan/5 transition"
                      >
                        <span className="font-body text-sm text-text-body group-hover:text-cyan transition truncate">
                          {item.title}
                        </span>
                        <ChevronRight className="w-4 h-4 text-cyan opacity-0 group-hover:opacity-100 flex-shrink-0 transition" />
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={onOpenServicesOverlay}
                      className="w-full px-2 py-2 font-body font-semibold text-cyan text-xs inline-flex items-center gap-1"
                    >
                      View all <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
